import { createSelector } from "reselect"
import { get, first, chain, pick, map, filter } from "lodash"
import { getPortfolio as getCurrentPortfolio, getPortfolioType, getPortfolioLive, getFullPortfolioBenchmark } from "./portfolios"
import { getLatestDate } from "./dates"
import { getRawSelectedBase } from "."
import { getPortfoliosWithMetadata, getBenchmarksLookup } from "selectors/portfolios"
import { getRecentInstruments } from "selectors/recentInstruments"
import { getIndices } from "selectors/indices"
import { computeBaseHistory, computeBaseWeight, computeSelectedBase, makeFindAndGetBaseMetadata, searchBase, filterAndOrderBaseInstruments } from "helpers/base"
import { getBaskets } from "resources/baskets"

const EMPTY_ARRAY = []
const getAvailableBenchmarks = state => get(state, "global.data.benchmarksAndPortfolios.data.model.benchmarks")
const getBaseSearchValue = state => get(state, "modules.portfolioRoot.searchValue")
const getPreviousModelBase = state => get(state, "modules.portfolioRoot.previousModelSelectedBaseId")
const getRawExperiments = state => get(state, "global.data.experiments.data")
const getRawResearchTopics = state => get(state, "global.data.accountResearchTopics.data.researchTopics")
const getRawETFs = state => get(state, "global.data.etfs.data", EMPTY_ARRAY)
const getRawBaseHistory = state => get(state, "modules.portfolioRoot.baseHistory")

const getETFs = createSelector(
  [getRawETFs],
  (etfs) => map(etfs, etf => ({
    ...pick(etf, ["ticker", "mic", "description", "availableFrom", "internalId", "hasComposition"]),
    id: `${etf.ticker}.${etf.mic}`,
  })),
)

// Valid portfolios for bases are
// - Not the current portfolio
// - Have an availableFrom
// - If the current portfolio is not live, the portfolio availableFrom must be greater or equal to the latestDate
const getValidAsBasePortfolios = createSelector(
  [getPortfoliosWithMetadata, getCurrentPortfolio, getLatestDate],
  (portfolios, currentPortfolio, latestDate) => filter(portfolios, ({ id, availableFrom }) => id !== get(currentPortfolio, "id") && availableFrom && (get(currentPortfolio, "live") || latestDate >= availableFrom)),
)

const getExperiments = createSelector(
  [getRawExperiments, getRawResearchTopics],
  (experiments, topics) => {
    return map(experiments, (experiment) => ({
      ...experiment,
      topic: chain(topics)
        .find(({ id }) => id === experiment.resourceId)
        .pick(["name", "description"])
        .value(),
    }))
  },
)
const getComputedWeight = createSelector(
  [getPortfolioType],
  (portfolioType) => computeBaseWeight(portfolioType),
)

export const getSearchResults = createSelector(
  [getBaseSearchValue, getComputedWeight, getAvailableBenchmarks, getValidAsBasePortfolios, getIndices, getExperiments, getETFs, getBaskets],
  (searchValue, weight, benchmarks, portfolios, indices, experiments, etfs, baskets) => searchBase(searchValue, weight, { experiments, benchmarks, portfolios, indices, etfs, baskets }),
)

const getRawBaseHistoryWithRecentInstruments = createSelector(
  [getRecentInstruments, getRawBaseHistory],
  filterAndOrderBaseInstruments,
)

const getRawBaseHistoryMetadata = createSelector(
  [getRawBaseHistory, getComputedWeight, getBenchmarksLookup, getValidAsBasePortfolios, getIndices, getExperiments, getETFs, getBaskets],
  (baseHistory, weight, benchmarks, portfolios, indices, experiments, etfs, baskets) => computeBaseHistory(baseHistory, weight, { benchmarks, portfolios, indices, experiments, etfs, baskets }),
)

export const getBaseHistory = createSelector(
  [getRawBaseHistoryWithRecentInstruments, getComputedWeight, getBenchmarksLookup, getValidAsBasePortfolios, getIndices, getExperiments, getETFs, getBaskets],
  (baseHistory, weight, benchmarks, portfolios, indices, experiments, etfs, baskets) => computeBaseHistory(baseHistory, weight, { benchmarks, portfolios, indices, experiments, etfs, baskets }),
)

export const getLastUsedBenchmark = createSelector(
  [getRawBaseHistoryMetadata, getLatestDate, getPortfoliosWithMetadata, getPortfolioLive],
  (baseHistory) => first(baseHistory),
)

export const getPreviousModelBaseUsed = createSelector(
  [getPreviousModelBase, getComputedWeight, getBenchmarksLookup, getValidAsBasePortfolios, getIndices, getExperiments, getETFs],
  (previousModelBase, weight, benchmarks, portfolios, indices, experiments, etfs) => makeFindAndGetBaseMetadata(weight, { benchmarks, portfolios, indices, experiments, etfs })(previousModelBase),
)

export const getSelectedBase = createSelector(
  [getRawBaseHistoryMetadata, getRawSelectedBase, getLastUsedBenchmark, getPreviousModelBaseUsed],
  computeSelectedBase,
)

export const computeDefaultSelectedBase = createSelector(
  [getFullPortfolioBenchmark, getLastUsedBenchmark],
  (portfolioBenchmark, lastUsedBenchmark) => lastUsedBenchmark || portfolioBenchmark,
)

export const getBaseStartDate = createSelector(
  [getFullPortfolioBenchmark, getSelectedBase],
  (portfolioBenchmark, selectedBase) => {
    const base = selectedBase || portfolioBenchmark
    return base.availableFrom
  },
)
